import { useState } from "react";
import {
  Popover,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { HelpContent } from "../config/helpContent";
import { getSiteHref } from "../config/siteLinks";
import { HelpSearch } from "./HelpSearch";
import { useDataEnvironment } from "../contexts/DataEnvironmentContext";
import { LoadingState } from "./common/LoadingStates";

interface HelpPopoverProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  helpContent: HelpContent;
  currentPath: string;
}

export const HelpPopover = ({
  open,
  onClose,
  anchorEl,
  helpContent,
  currentPath
}: HelpPopoverProps) => {
  const { dataEnvironment } = useDataEnvironment();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          overflow: "hidden",
          mt: 0.75,
        },
      }}
    >
      <Box
        sx={{
          width: { xs: "min(94vw, var(--overlay-width-wide))", sm: "var(--overlay-width-wide)" },
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 0.75
        }}>
          <Typography variant="h6" component="span">
            Help
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ ml: 2 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ px: 2.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Page Help" />
          <Tab label="Search" />
        </Tabs>

        <DialogContent
          sx={{
            px: "var(--overlay-body-padding)",
            py: "var(--overlay-body-padding)",
            minHeight: 200,
            maxHeight: "60vh",
            overflow: "auto",
            boxSizing: "border-box",
          }}
        >
          {activeTab === 0 && (
            <Box sx={{ minHeight: 200 }}>
              {/* Debug info */}
              {/* <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}
              >
                <Typography variant="caption" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Debug Info:
                </Typography>
                <Typography variant="caption" component="div">
                  Path: {currentPath}
                </Typography>
              </Box> */}

              {/* Loading state */}
              {helpContent.loading && (
                <LoadingState label="Loading Help" minHeight={200} size="compact" />
              )}

              {/* Error state */}
              {helpContent.error && !helpContent.loading && (
                <Typography variant="body2" color="text.secondary">
                  Help content not found for topic: {helpContent.slug || 'unknown'}
                </Typography>
              )}

              {/* Help content */}
              {!helpContent.loading && !helpContent.error && (
                <Stack spacing={1}>
                  {/* <Typography variant="h6" gutterBottom>
                    {helpContent.title}
                  </Typography>
                  <Divider sx={{ mb: 2 }} /> */}
                  <Box
                    sx={{
                      '& p': {
                        marginTop: 0,
                        marginBottom: 1,
                        lineHeight: 1.6
                      },
                      '& p:last-child': {
                        marginBottom: 0
                      },
                      '& strong': {
                        fontWeight: 600
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: helpContent.body }}
                  />
                  {helpContent.guideHref && (
                    <Typography variant="body2">
                      <a
                        href={getSiteHref(helpContent.guideHref, dataEnvironment)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        Read the full guide <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </a>
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ minHeight: 200 }}>
              <HelpSearch />
            </Box>
          )}
        </DialogContent>
      </Box>
    </Popover>
  );
};
